import {
  Action,
  Actions,
  DockLocation,
  Layout,
  TabNode
} from 'flexlayout-react';
import { useCallback, useEffect, useMemo } from 'react';
import { createSymbolModel } from './layouts';
import { useChartState } from '@client/state/chart-state.hook';
import { useObservable } from 'rxjs-hooks';
import { LayoutProvider } from './layout-context';
import { SymbolLayoutContent } from './symbol-layout-content';
import { Box } from '@mui/material';

export const SymbolLayout = () => {
  const { getState$, dispatch } = useChartState();
  const symbolModel = useMemo(() => createSymbolModel(), []);
  const chartState = useObservable(() => getState$(), new Map());

  useEffect(() => {
    return () => {
      dispatch({ type: 'CLEAR_STATE' });
    };
  }, [dispatch]);

  const handleDeleteSymbol = useCallback(
    (action: Action) => {
      if (action.type !== 'FlexLayout_DeleteTab') {
        return action;
      }

      const tabId = action.data.node;
      if (!tabId) {
        console.error('Widget ID is missing during tab deletion');
        return action;
      }

      dispatch({
        type: 'DELETE_CHART',
        payload: { id: tabId }
      });

      return action;
    },
    [dispatch]
  );

  useEffect(() => {
    // Keep FlexLayout tabs in sync with the source-of-truth chart state.
    // Important: when the user changes a chart's `symbol`, the chart `id` stays the same
    // (so the tab still exists), but the tab label should change to reflect the new symbol.
    // Remove tabs that no longer exist in chart state
    const mainTabSet = symbolModel.getNodeById('main-tabs') as any;
    const tabChildren: any[] = mainTabSet?.getChildren?.() ?? [];
    tabChildren.forEach((tabNode: any) => {
      if (!chartState.has(tabNode.getId())) {
        symbolModel.doAction(Actions.deleteTab(tabNode.getId()));
      }
    });

    if (chartState.size === 0) return;

    Array.from(chartState.entries()).forEach(([id, state]) => {
      const node = symbolModel.getNodeById(id);
      const { symbol } = state.formState.marketConfig;

      if (node && node instanceof TabNode) {
        // Only TabNode has `getName/getComponent`. Update in-place to avoid recreating tabs
        // (which would reset layout/selection), and to prevent stale symbol labels.
        if (node.getName() !== symbol || node.getComponent() !== symbol) {
          symbolModel.doAction(
            Actions.updateNodeAttributes(id, {
              name: symbol,
              component: symbol
            })
          );
        }
        return;
      }

      // New chart id -> create a new symbol tab.
      symbolModel.doAction(
        Actions.addNode(
          {
            type: 'tab',
            name: symbol,
            component: symbol,
            id
          },
          'main-tabs',
          DockLocation.CENTER,
          -1
        )
      );
    });

    // Keep the root tabset alive even if all tabs are closed,
    // so we can add new symbols without recreating the model.
    symbolModel.doAction(
      Actions.updateModelAttributes({
        tabSetEnableDeleteWhenEmpty: false
      })
    );
  }, [chartState, symbolModel]);

  const factory = useCallback((node: TabNode) => {
    const component = node.getComponent();
    if (!component) {
      console.warn(`No component found for tab node: ${node.getId()}`);
      return null;
    }

    return (
      <LayoutProvider id={node.getId()}>
        <SymbolLayoutContent />
      </LayoutProvider>
    );
  }, []);

  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Layout
        model={symbolModel}
        factory={factory}
        onAction={handleDeleteSymbol}
      />
    </Box>
  );
};
